import {EventEmitter} from 'events';

import kurentoUtils from 'kurento-utils';
import {some} from 'lodash';
import {useState, useEffect, useCallback, useMemo, ReactElement} from 'react';
import {SocketLikeWithNamespace} from 'socket.io-react-hook/lib/cjs/types';

import {SocketEvent} from 'enums';
import {useGroupSessionSocket, useIsChanged, useSocketEventCallback} from 'hooks';
import {logger, renderUtils, userAuthUtils} from 'utils';

import {LocalParticipant} from './LocalParticipant';
import {Participant} from './Participant';
import {PeerConnectionId} from './constants';

enum ServerEvent {
    ParticipantConnected = 'participant-connected',
    ParticipantDisconnected = 'participant-disconnected',
    ParticipantStartPeerConnection = 'participant-start-peer-connection',
    StartPeerConnectionSuccess = 'start-peer-connection:success',
    ReceiveSdpAnswer = 'receive-sdp-answer',
    IceCandidate = 'ice-candidate',
    JoinRoomSuccess = 'join-room:success',
    JoinRoomError = 'join-room:error',
}

enum ParticipantEvent {
    ReceiveSdpOffer = 'receive-sdp-offer',
    OnIceCandidate = 'on-ice-candidate',
    StartPeerConnection = 'start-peer-connection',
    StopPeerConnection = 'stop-peer-connection',
    JoinRoom = 'join-room',
    LeaveRoom = 'leave-room',
}

export enum RoomEvent {
    Connected = 'connected',
    Disconnected = 'disconnected',
    ConnectError = 'connect-error',
    PeerConnectionReady = 'peer-connection-ready',
}

export enum RoomState {
    Connected = 'connected',
    ConnectError = 'connect-error',
    Connecting = 'connecting',
    Disconnected = 'disconnected'
}

type MediaElementProperties = {
    muted?: boolean;
}

type WebRTCConfiguration = {
    iceServers: {
        urls: string;
        username: string;
        credential: string;
    }[];
};

export type WebRTCPeerOptions = {
    mainRemoteParticipantElement?: HTMLElement;
    localParticipantElement?: HTMLElement;
    otherRemoteParticipantsElement?: HTMLElement;
    constraints?: MediaStreamConstraints;
    audiooutput?: string;
    videoStream?: MediaStream;
    mediaElementProperties?: MediaElementProperties;
}

export class GroupRoomManager {
    public localParticipant: LocalParticipant;

    private participants: Map<string, Participant> = new Map();
    private emitter = new EventEmitter();
    private joinRoomState = RoomState.Disconnected;
    private configuration: WebRTCConfiguration;

    constructor(private socket: SocketLikeWithNamespace, private token: string) {
        if (!token) {
            throw new Error('Token is required to initialize room manager');
        }
    }

    get isRoomStateConnected() {
        return this.joinRoomState === RoomState.Connected;
    }

    // event emitter functions
    on = (event: RoomEvent, fn) => {
        this.emitter.on(event, fn);
        return () => this.off(event, fn);
    };

    once = (event: RoomEvent, fn) => {
        this.emitter.once(event, fn);
        return () => this.off(event, fn);
    };

    off = (event: RoomEvent, fn) => this.emitter.off(event, fn);

    private emit = (event, payload?) => this.emitter.emit(event, payload);

    connect() {
        if (this.joinRoomState === RoomState.Disconnected) {
            this.setJoinRoomState(RoomState.Connecting);
            this.subscribeSocketEvents();
            this.sendMessage(ParticipantEvent.JoinRoom, {token: this.token});
        }
        return this;
    }

    disconnect() {
        this.sendMessage(ParticipantEvent.LeaveRoom);

        this.participants.forEach((p, name) => {
            this.disconnectParticipant(name);
        });

        this.localParticipant?.dispose();
        this.localParticipant = null;
        this.participants.clear();
        this.unsubscribeSocketEvents();
        this.setJoinRoomState(RoomState.Disconnected);
    }

    onStartParticipantPeerConnection(data: {participantName: string, peerId: PeerConnectionId}) {
        this.receiveMediaStream(data);
    }

    handleJoinRoomSuccess(data: {
        roomName: string,
        participantName: string,
        participants: {participantName: string, peerIds: string[]}[],
        configuration: WebRTCConfiguration
    }) {
        const localParticipant = new LocalParticipant(this, data.participantName);
        this.participants.set(localParticipant.name, localParticipant);
        this.localParticipant = localParticipant;
        this.configuration = data.configuration;
    }

    async onStartPeerConnectionSuccess(data: {peerId: PeerConnectionId, participants: string[]}) {
        const {localParticipant} = this;
        const peer = this.localParticipant.getPeerConnection(data.peerId);
        peer.started = true;
        const {options} = peer;

        if (options.constraints?.video || options.constraints?.audio || options.videoStream) {
            const webRTCPeerOptions = {
                localVideo: peer.getMediaElement(),
                videoStream: options.videoStream,
                mediaConstraints: options.constraints,
                onicecandidate: (candidate) => this.onIceCandidate({participantName: localParticipant.name, peerId: peer.id, candidate}),
                configuration: this.configuration
            };
            const emit = this.emit;
            peer.rtcPeer = await kurentoUtils.WebRtcPeer.WebRtcPeerSendonly(webRTCPeerOptions, (e) => {
                if (e) {
                    return logger.error(e);
                }
                peer.rtcPeer.generateOffer((error, sdpOffer) =>
                    this.offerToReceiveMediaStream({participantName: localParticipant.name, peerId: peer.id, sdpOffer, error}));
                emit(RoomEvent.PeerConnectionReady, {peerId: data.peerId, room: this});
            });
        }

        data.participants.forEach((participantName: string) => {
            this.receiveMediaStream({participantName, peerId: data.peerId});
        });
    }

    startPeerConnection(id: PeerConnectionId, options: WebRTCPeerOptions) {
        const peer = this.localParticipant.addPeerConnection(id, options);
        peer.attach(options.localParticipantElement);
        this.sendMessage(ParticipantEvent.StartPeerConnection, {peerId: id});
    }

    stopPeerConnection(id: PeerConnectionId) {
        Array.from(this.participants.values()).filter((p) => some(p.peers, {id})).forEach((participant) => {
            participant.removePeerConnection(id);
        });
        this.sendMessage(ParticipantEvent.StopPeerConnection, {peerId: id});
    }

    stopLocalPeerConnection(id: PeerConnectionId) {
        this.localParticipant.removePeerConnection(id);
        this.sendMessage(ParticipantEvent.StopPeerConnection, {peerId: id});
    }

    onReceiveSdpAnswer(data: {participantName: string, peerId: PeerConnectionId, sdpAnswer: string}) {
        const participant = this.participants.get(data.participantName);
        if (participant) {
            const peer = participant.getPeerConnection(data.peerId);
            if (peer) {
                peer.rtcPeer.processAnswer(data.sdpAnswer, (error) => {
                    if (error) {
                        logger.error(error);
                    }
                });
            } else {
                logger.error(
                    {category: logger.category.WebRTC, message: 'Unable to find participant peerId'},
                    {participantName: data?.participantName, peerId: data?.peerId}
                );
            }
        } else {
            logger.warn({category: logger.category.WebRTC, message: 'Error receive sdp answer - no participant'}, data);
        }
    }

    onNewIceCandidate(data: {participantName: string, peerId: PeerConnectionId, candidate: RTCIceCandidate}) {
        const participant = this.participants.get(data.participantName);
        if (participant) {
            const peer = participant.getPeerConnection(data?.peerId);
            if (peer) {
                peer.rtcPeer.addIceCandidate(data.candidate, (error) => {
                    if (error) {
                        logger.error({category: logger.category.WebRTC, message: 'Error adding ice candidate'}, data);
                    }
                });
            } else {
                logger.error(
                    {category: logger.category.WebRTC, message: 'Unable to find participant peerId'},
                    {participantName: data?.participantName, peerId: data?.peerId}
                );
            }
        } else {
            logger.error({category: logger.category.WebRTC, message: 'NewIceCandidate failed - no participant'}, data);
        }
    }

    onParticipantDisconnected(data: {participantName: string}) {
        this.disconnectParticipant(data.participantName);
    }

    private subscribeSocketEvents() {
        this.socket
            .on(ServerEvent.JoinRoomSuccess, this.onJoinRoomSuccess)
            .on(ServerEvent.JoinRoomError, this.onJoinRoomError)
            .on(SocketEvent.Disconnect, this.onSocketDisconnect);
    }

    private unsubscribeSocketEvents() {
        this.socket
            .off(ServerEvent.JoinRoomSuccess, this.onJoinRoomSuccess)
            .off(ServerEvent.JoinRoomError, this.onJoinRoomError)
            .off(SocketEvent.Disconnect, this.onSocketDisconnect);
    }

    private onJoinRoomError = (message: any) => {
        this.setJoinRoomState(RoomState.ConnectError);
        this.emit(RoomEvent.ConnectError, message.data);
    };

    private onJoinRoomSuccess = (message) => {
        this.handleJoinRoomSuccess(message.data);
        this.setJoinRoomState(RoomState.Connected);
        this.emit(RoomEvent.Connected, this);
    };

    private onSocketDisconnect = () => {
        this.setJoinRoomState(RoomState.Disconnected);
        this.emit(RoomEvent.Disconnected, this);
        this.disconnect();
    };

    private setJoinRoomState(state: RoomState) {
        this.joinRoomState = state;
    }

    private sendMessage(event: ParticipantEvent, data?: {[key: string]: any}) {
        if (this.socket?.connected) {
            this.socket?.emit(event, data);
        } else {
            logger.warn({category: logger.category.WebRTC, message: 'Skip sending message due to socket disconnected'}, event);
        }
    }

    private receiveMediaStream(data: {participantName: string, peerId: PeerConnectionId}) {
        const {participantName, peerId} = data;
        const localParticipantPeer = this.localParticipant.getPeerConnection(peerId);
        if (!localParticipantPeer.started) {
            return;
        }
        const hasMainRemoteParticipants = Array.from(this.participants.values()).filter((p) => some(p.peers, {peerId})).length > 1;
        const element = hasMainRemoteParticipants ?
            localParticipantPeer?.options.otherRemoteParticipantsElement : localParticipantPeer?.options.mainRemoteParticipantElement;
        const participant = this.participants.get(participantName) || new Participant(this, participantName);
        this.participants.set(participantName, participant);

        let remoteParticipantPeer = participant.getPeerConnection(peerId);
        if (!remoteParticipantPeer) {
            remoteParticipantPeer = participant.addPeerConnection(peerId, localParticipantPeer.options);
            remoteParticipantPeer.attach(element);
        }

        const mediaElement = remoteParticipantPeer.getMediaElement();

        if (mediaElement) {
            const options = {
                remoteVideo: mediaElement,
                onicecandidate: (candidate) => this.onIceCandidate({participantName: participant.name, peerId, candidate}),
                configuration: this.configuration
            };
            remoteParticipantPeer.rtcPeer = kurentoUtils.WebRtcPeer.WebRtcPeerRecvonly(options, (error) => {
                if (error) {
                    return logger.error(error);
                }
                remoteParticipantPeer.rtcPeer.generateOffer((error2, sdpOffer) =>
                    this.offerToReceiveMediaStream({participantName: participant.name, peerId, sdpOffer, error: error2}));
            });
        }
    }

    private offerToReceiveMediaStream = (data: {participantName: string, peerId: string, sdpOffer: string, error: any}) => {
        const {participantName, peerId, sdpOffer, error} = data;
        if (error) {
            return logger.error(
                {category: logger.category.WebRTC, message: 'Offer to receive media error'},
                {error, participantName, peerId, sdpOffer}
            );
        }
        this.sendMessage(ParticipantEvent.ReceiveSdpOffer, {participantName, peerId, sdpOffer});
    };

    private onIceCandidate = (data: {participantName: string, peerId: string, candidate: RTCIceCandidate}) => {
        this.sendMessage(ParticipantEvent.OnIceCandidate, data);
    };

    private disconnectParticipant(participantName: string) {
        const participant = this.participants.get(participantName);
        if (participant) {
            participant.dispose();
            this.participants.delete(participantName);
        } else {
            logger.warn(
                {category: logger.category.WebRTC, message: 'Disconnect unknown participant'},
                {participantName, participants: this.participants}
            );
        }
    }
}

type Props = {
    token: string;
    children?: (props: {room: GroupRoomManager | null}) => ReactElement;
    onRoomChange?: (room: GroupRoomManager) => void;
    onJoinRoomSuccess?: () => void;
}

export const KurentoRoomProvider: React.FC<Props> = ({token, children, onRoomChange, onJoinRoomSuccess}: Props) => {
    const path = useMemo(() => userAuthUtils.parseJwtPayload(token)?.path, [token]);
    const {socket, connected} = useGroupSessionSocket(token, {path, enabled: !!token});

    const connectedChanged = useIsChanged(connected);
    const tokenChanged = useIsChanged(token);
    const idChanged = useIsChanged(socket.id);
    const [room, setRoom] = useState<GroupRoomManager>();

    useEffect(() => logger.debug({
        category: logger.category.WebRTC,
        message: `Socket connection state changed. Connected: ${connected}. ID: ${socket.id}`
    }), [connected, socket.id]);

    useEffect(() => () => {
        if (room?.isRoomStateConnected) {
            room.disconnect();
        }
    }, [room]);

    useEffect(() => {
        if ((connectedChanged || tokenChanged || idChanged) && connected && socket?.id && token && !room) {
            const newRoom = new GroupRoomManager(socket, token);
            newRoom.connect();
            setRoom(newRoom);
        }
    }, [connectedChanged, tokenChanged, idChanged, connected, socket, room, token]);

    useSocketEventCallback(socket, ServerEvent.JoinRoomSuccess, useCallback(() => {
        onJoinRoomSuccess?.();
    }, [onJoinRoomSuccess]), {new: true});
    useSocketEventCallback(socket, ServerEvent.ParticipantStartPeerConnection,
        useCallback((data: {participantName: string, peerId: PeerConnectionId}) => {
            room?.onStartParticipantPeerConnection(data);
        }, [room]), {new: true});
    useSocketEventCallback(socket, ServerEvent.StartPeerConnectionSuccess,
        useCallback((data: {peerId: PeerConnectionId, participants: string[]}) => {
            room?.onStartPeerConnectionSuccess(data);
        }, [room]), {new: true});
    useSocketEventCallback(socket, ServerEvent.ParticipantDisconnected, useCallback((data: any) => {
        room?.onParticipantDisconnected(data);
    }, [room]), {new: true});
    useSocketEventCallback(socket, ServerEvent.ReceiveSdpAnswer, useCallback((data: any) => {
        room?.onReceiveSdpAnswer(data);
    }, [room]), {new: true});
    useSocketEventCallback(socket, ServerEvent.IceCandidate, useCallback((data: any) => {
        room?.onNewIceCandidate(data);
    }, [room]), {new: true});
    useSocketEventCallback(socket, SocketEvent.Connect, useCallback(() => {
        // used on server restart if a room already exist
        room?.connect();
    }, [room]));

    useEffect(() => {
        onRoomChange?.(room);
    }, [room, onRoomChange]);

    return (
        renderUtils.renderFunctionOrElementChildren(children, {room: connected && socket?.id ? room : null})
    );
};
